import { memo, useState } from "react";

import { ReactiveVar, useReactiveVar } from "@apollo/client";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import {
  DESTRUCTIVE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { logError } from "@hl/shared-features/lib/services/logger";
import { Button, Group, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";

import { IN_DEVELOPMENT, TOOLS_URL } from "../../../../../../config";
import ModalBase from "../../../../../layout/Modal/ModalBase";
import {
  Modal,
  ModalType,
  modalVar,
  resetModalVar,
} from "../../../../../layout/Modal/modal";
import {
  useCollectionDeleteMutation,
  useDeleteImportedTokenMutation,
} from "../../queries.graphql.generated";

export enum ImportedEntityType {
  COLLECTION = "Collection",
  TOKEN = "Token",
}

export interface DeleteImportModalData {
  id: string;
  importedEntityType: ImportedEntityType;
  collectionId?: string;
}

export type DeleteImportModalProps = ReactiveVar<Modal<DeleteImportModalData>>;

const DeleteImportModal: React.FC = () => {
  const navigate = useNavigate();
  const { showModal, data } = useReactiveVar(
    modalVar as DeleteImportModalProps
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [deleteCollectionMutation] = useCollectionDeleteMutation();
  const [deleteImportedTokenMutation] = useDeleteImportedTokenMutation();

  const handleDeleteImport = async () => {
    try {
      setIsDeleting(true);
      // imported collection, not migrated to tokens yet
      data?.collectionId
        ? await deleteCollectionMutation({
            variables: {
              id: data?.id,
            },
          })
        : await deleteImportedTokenMutation({
            variables: {
              tokenId: data?.id,
            },
          });
      resetModalVar();
      navigate(TOOLS_URL.home);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      await logError({ error, message: "delete failed" });
      IN_DEVELOPMENT && console.log("🚨 [ERROR][HANDLE_DELETE]: ", error);
    }
  };

  return (
    <ModalBase
      isOpen={showModal === ModalType.DELETE_IMPORT}
      title={"Remove Imported " + data?.importedEntityType.toString()}
      canClose={false}
    >
      <div>
        <Text>
          Are you sure you want to remove this imported{" "}
          {data?.importedEntityType.toString().toLowerCase()}?
        </Text>
        <ModalSection>
          <Group noWrap>
            <Button
              fullWidth
              color={SECONDARY_COLOR}
              size="xl"
              onClick={resetModalVar}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              color={DESTRUCTIVE_COLOR}
              size="xl"
              loading={isDeleting}
              onClick={handleDeleteImport}
            >
              Remove {data?.importedEntityType.toString()}
            </Button>
          </Group>
        </ModalSection>
      </div>
    </ModalBase>
  );
};

export default memo(DeleteImportModal);
